import { SESSION_STORAGE_KEY, type Username } from '../utils/constants';

export type ApiSession = {
  username: Username;
  token: string;
};

export type UserStatus = {
  username: Username;
  lastSeenAt: number;
  notificationPermission: NotificationPermission | 'unsupported';
};

export type StatusResponse = {
  success: true;
  currentUser: UserStatus;
  otherUser: UserStatus | null;
  currentUserHasNotifications: boolean;
  receiverHasNotifications: boolean;
  currentUserDeviceCount: number;
  receiverDeviceCount: number;
};

export type PushDebugResponse = {
  success: true;
  username: Username;
  cloudflare: {
    hasKvBinding: boolean;
    hasPasswordSecret: boolean;
    hasVapidPublicKey: boolean;
    hasVapidPrivateKey: boolean;
    hasVapidSubject: boolean;
  };
  push: {
    hasStoredSubscription: boolean;
    hasEndpoint: boolean;
    hasP256dh: boolean;
    hasAuth: boolean;
    updatedAt: number | null;
    deviceCount: number;
  };
};

export type PushDelivery = {
  accepted: true;
  status: number;
  attempts: number;
  acceptedDevices?: number;
  totalDevices?: number;
};

export type EmergencyAlert = {
  id: string;
  sender: Username;
  receiver: Username;
  createdAt: number;
  expiresAt: number;
  status: 'active' | 'acknowledged';
  acknowledgedAt?: number;
  pushStatus: 'accepted' | 'failed' | 'not_registered';
  pushAttempts: number;
  pushError?: string;
};

type ApiErrorResponse = {
  success: false;
  error: string;
  code?: string;
  detail?: string;
};

export function loadStoredSession(): ApiSession | null {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<ApiSession>;

    if ((parsed.username === 'user1' || parsed.username === 'user2') && typeof parsed.token === 'string') {
      return {
        username: parsed.username,
        token: parsed.token,
      };
    }

    return null;
  } catch {
    return null;
  }
}

export function storeSession(session: ApiSession) {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession() {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

export async function login(username: Username, password: string) {
  const response = await apiFetch<{ success: true; username: Username; csrfToken: string }>('/api/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });

  return {
    username: response.username,
    token: response.csrfToken,
  };
}

export async function logout(token: string) {
  await apiFetch<{ success: true }>('/api/logout', {
    method: 'POST',
    token,
  });
}

export async function me(token?: string | null) {
  return apiFetch<{ success: true; username: Username; csrfToken: string }>('/api/me', { token: token ?? undefined });
}

export async function getStatus(token: string) {
  return apiFetch<StatusResponse>('/api/status', { token });
}

export async function getPushPublicKey() {
  const response = await apiFetch<{ success: true; publicKey: string }>('/api/push-public-key');
  return response.publicKey;
}

export async function registerDevice(
  token: string,
  deviceId: string,
  deviceLabel: string,
  subscription: PushSubscriptionJSON,
  notificationPermission: NotificationPermission | 'unsupported',
) {
  return apiFetch<{ success: true; username: Username }>('/api/register-device', {
    method: 'POST',
    token,
    body: JSON.stringify({ deviceId, deviceLabel, subscription, notificationPermission }),
  });
}

export async function sendSos(token: string) {
  return apiFetch<{
    success: true;
    alert: EmergencyAlert;
    delivery?: PushDelivery;
    warning?: string;
  }>('/api/send-sos', {
    method: 'POST',
    token,
  });
}

export async function sendTestPush(token: string, deviceId: string) {
  return apiFetch<{ success: true; username: Username; createdAt: number; delivery: PushDelivery }>(`/api/test-push?deviceId=${encodeURIComponent(deviceId)}`, {
    method: 'POST',
    token,
  });
}

export async function getPushDebug(token: string, deviceId: string) {
  return apiFetch<PushDebugResponse>(`/api/debug-push?deviceId=${encodeURIComponent(deviceId)}`, { token });
}

export async function getAlerts(token: string) {
  return apiFetch<{ success: true; incoming: EmergencyAlert | null; outgoing: EmergencyAlert | null }>('/api/alerts', {
    token,
  });
}

export async function acknowledgeAlert(token: string, alertId: string) {
  return apiFetch<{ success: true; alert: EmergencyAlert }>('/api/ack-alert', {
    method: 'POST',
    token,
    body: JSON.stringify({ alertId }),
  });
}

async function apiFetch<T>(path: string, options: RequestInit & { token?: string } = {}) {
  const headers = new Headers(options.headers);
  headers.set('accept', 'application/json');

  if (options.body) {
    headers.set('content-type', 'application/json');
  }

  if (options.token) {
    headers.set('x-csrf-token', options.token);
  }

  const response = await fetch(path, {
    ...options,
    headers,
    credentials: 'same-origin',
  });
  const data = (await response.json().catch(() => null)) as T | ApiErrorResponse | null;

  if (!response.ok) {
    const errorMessage = data && typeof data === 'object' && 'error' in data ? data.error : `Request failed with HTTP ${response.status}.`;
    const errorCode = data && typeof data === 'object' && 'code' in data && typeof data.code === 'string' ? data.code : '';
    const errorDetail = data && typeof data === 'object' && 'detail' in data && typeof data.detail === 'string' ? data.detail : '';

    if (errorCode) {
      throw new Error(`${errorMessage} (${errorCode})${errorDetail ? ` - ${errorDetail}` : ''}`);
    }

    throw new Error(errorMessage);
  }

  return data as T;
}
